import * as React from 'react';
import { Link } from 'react-router-dom';
import type { IUserJSON } from 'external-api-types';
import { Avatar as CoreAvatar } from '@netlify/ui';
import Avatar from '../common/Avatar/Avatar';
import HybridLink from '../common/HybridLink/HybridLink';
import List from '../common/List/List';
import ListItem from '../common/List/ListItem/ListItem';
import Tooltip from '../common/Tooltip/Tooltip';
import UserService from '../../modules/UserService';
import { useVariableValue } from '../../types/dvcVariableTypes';
import { useAppSelector } from '../../store/app';
import cn from '~modules/cn';
import AccountSettingsIcon from '../../assets/icons/v2design/basics/user.svg';
import LogoutIcon from '../../assets/icons/v2design/basics/logout.svg';
import './user-panel.scss';

export type UserPanelProps = {
    user: IUserJSON;
    disableSignin?: boolean;
    className?: string;
};

export const UserPanel = (props: UserPanelProps): JSX.Element | null => {
    const { user, disableSignin } = props;
    const postLoginRedirect = encodeURIComponent(window.location.href.replace(window.location.origin, ''));

    const [isUserMenuOpen, setIsUserMenuOpen] = React.useState(false);
    const userModalRef = React.useRef<HTMLDivElement>(null);
    const project_sealeo_ui = useVariableValue('project_sealeo_ui', false);
    const coreUser = useAppSelector((state) => state.global.coreUser);

    // If a user has not yet verified their email, we don't show them the account dropdown menu.
    // If login is disabled for an anonymous user.
    if ((!user && disableSignin) || (user && !UserService.isEmailConfirmed(user))) {
        return null;
    }
    // Logged out or new users see the sign in button
    else if (!user) {
        return (
            <div className="user-panel">
                <Link to={`/login?redirect=${postLoginRedirect}`}>Sign In</Link>
            </div>
        );
    }

    // Logged-in users with a verified email
    let username = null;
    if (user.emailVerification === 'pending') {
        username = `${user.unverifiedEmail} (unverified)` || 'Unverified';
    } else if (user.email) {
        username = user.email;
    }

    return (
        <>
            {isUserMenuOpen && (
                <Tooltip
                    for={userModalRef}
                    appearanceClassNames={['tooltip--no-background', 'tooltip--box-shadow', 'tooltip--no-max-width', 'tooltip--no-padding']}
                    tooltipProps={{
                        trigger: 'click',
                        visible: isUserMenuOpen,
                        onVisibleChange: () => {
                            setIsUserMenuOpen(false);
                        }
                    }}
                    placement={project_sealeo_ui ? 'bottom-end' : undefined}
                >
                    <List>
                        <HybridLink
                            to="/account"
                            onClick={() => {
                                setIsUserMenuOpen(false);
                            }}
                            data-testid="user-panel-link-account"
                        >
                            <ListItem Tag="div" leftView={<AccountSettingsIcon />} text="Account" />
                        </HybridLink>
                        <HybridLink
                            to="/logout"
                            onClick={() => {
                                setIsUserMenuOpen(false);
                            }}
                            data-testid="user-panel-link-account"
                        >
                            <ListItem Tag="div" leftView={<LogoutIcon />} text="Logout" innerTestId="user-panel-button-logout" />
                        </HybridLink>
                    </List>
                </Tooltip>
            )}
            <div
                className={cn('user-panel', props.className)}
                ref={userModalRef}
                onClick={() => {
                    setIsUserMenuOpen(true);
                }}
                data-testid="user-panel-button"
            >
                {project_sealeo_ui && coreUser ? (
                    <CoreAvatar
                        user={coreUser}
                        hoverTooltip={coreUser.email}
                        className="tw-h-avatar-md tw-w-avatar-md tw-leading-[theme('height.avatar-md')]"
                        size="md"
                    />
                ) : (
                    <>
                        <Avatar color="light" nameOrEmail={user.displayName ?? user.email} dataTip={user.email} />
                        <span className="user-panel-email">{username}</span>
                    </>
                )}
            </div>
        </>
    );
};
